/**
 * ./exercise9/Exercise9Workout.js
 *
 * This module is similar with exercise7 and exercise8.
 * The only difference is Exercise 9's text flashes by
 * highlighting words dynamically while the rest of the
 * text remains dimmed.
 *
 */

import React, { useState, useEffect } from "react";
import "./Exercise9Workout.css"; // Import the CSS file
import { useNavigate } from "react-router-dom";
import CloseIcon from "../components/icons/close.svg";
import InfoIcon from "../components/icons/InfoIcon.svg";
import LibraryButton from "../exercise7/icons/LibraryButton.svg";
import UploadButton from "../exercise7/icons/UploadButton.svg";
import PlayButton from "../components/icons/play.svg";
import UpButton from "../components/icons/UpButton.svg";
import DownButton from "../components/icons/DownButton.svg";
import { updateUserProgress } from "../utility";
import { DEBUG_TIME, API_BASE_URL } from "../config";

const Exercise9Workout = () => {
  const navigate = useNavigate();

  const TIMER_DURATION = DEBUG_TIME || 60;

  const [exerciseStep, setExerciseStep] = useState("Select Level");
  const [text, setText] = useState(["No Text"]);
  const [progress, setProgress] = useState(0);
  const [phrasesIndex, setPhrasesIndex] = useState(0);
  const [paragraphIndex, setParagraphIndex] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const [flashCount, setFlashCount] = useState(0);
  const [articleIndex, setArticleIndex] = useState(0);
  const [endArticleIndex, setEndArticleIndex] = useState(4);
  const [levelSelection, setLevelSelection] = useState(2);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [articleName, setArticleName] = useState("");
  const levels = [2, 4, 6, 8, 10];

  const [userData, setUserData] = useState(null);
  const [bookData, setBookData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [uuid, setUuid] = useState("");

  // Split the text into paragraphs and then into phrases.
  // Each paragraph becomes an array of phrases.
  const phraseSplitter = (text) => {
    const words = [];
    const paragraphs = [];
    for (let i = 0; i < text.length; i++) {
      words.push(text[i].split(" "));
      let paragraphPhrases = [];
      for (let j = 0; j < words[i].length; j += levelSelection) {
        paragraphPhrases.push(words[i].slice(j, j + levelSelection).join(" "));
      }
      // Optional: add an empty string as a pause at the end of the paragraph.
      paragraphPhrases.push("");
      paragraphs.push(paragraphPhrases);
    }
    return paragraphs;
  };

  // This produces an array of paragraphs (each paragraph is an array of phrases).
  let paragraphs = phraseSplitter(text);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchBookData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/book/books`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const data = await response.json();
        setBookData(data.books);
        setArticles(data.bookOrder);
      } catch (error) {
        console.error("Error fetching book data:", error);
      }
    };

    fetchUserData();
    fetchBookData();
  }, []);

  const isLevelUnlocked = (levelNumber) => {
    if (!userData) return false; // Assume locked if user data isn't loaded
    return userData.exercisesUnlocked[6] >= levelNumber;
  };

  useEffect(() => {
    if (
      isFlashing &&
      paragraphIndex < paragraphs.length &&
      phrasesIndex < paragraphs[paragraphIndex].length - 1 &&
      timeElapsed < TIMER_DURATION
    ) {
      const intervalId = setInterval(() => {
        setPhrasesIndex((prevIndex) => prevIndex + 1);
        setTimeElapsed((prevTime) => {
          const newTime = prevTime + 0.3;
          setProgress((newTime / TIMER_DURATION) * 100);
          return newTime;
        });

        // If we're at the end of the current paragraph (minus one because of the empty string)
        if (
          paragraphIndex < paragraphs.length - 1 &&
          phrasesIndex === paragraphs[paragraphIndex].length - 2
        ) {
          setPhrasesIndex(0);
          setParagraphIndex((prevIndex) => prevIndex + 1);
        }
      }, 300);

      return () => clearInterval(intervalId);
    }

    if (timeElapsed >= TIMER_DURATION) {
      setIsFlashing(false);
      const levels = [2, 4, 6, 8, 10];
      const timeoutId = setTimeout(() => {
        navigate("/exercise9test", {
          state: {
            articleText: text,
            articleName: articleName,
            levelSelection: levelSelection,
          },
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    isFlashing,
    phrasesIndex,
    paragraphs.length,
    timeElapsed,
    navigate,
    text,
    articleName,
    levelSelection,
  ]);

  const handleClose = () => {
    navigate("/");
  };

  const goToLibrary = () => {
    setExerciseStep("Choose Article");
  };

  const clickBook = (articleText, articleName) => {
    setText(articleText);
    setExerciseStep("Workout");
    setArticleName(articleName);
  };

  // When selecting a book from the library, resume progress.
  const clickBookModern = (bookUuid) => {
    setText(bookData[bookUuid].text);
    setExerciseStep("Workout");
    setArticleName(bookData[bookUuid].title);
    setUuid(bookUuid);
    let pghInd = 0,
      pIndex = userData.progress[bookUuid];
    let found = false;
    for (const pgh of phraseSplitter(bookData[bookUuid].text)) {
      for (let i = 0; i < pgh.length; i++) {
        const textLen = pgh[i].split(" ").length;
        if (textLen > pIndex) {
          pIndex = i;
          found = true;
          break;
        }
        pIndex -= textLen;
      }
      if (found) break;
      pghInd++;
    }
    setPhrasesIndex(pIndex);
    setParagraphIndex(pghInd);
  };

  // When stopping, update progress by computing total words read.
  const playWordFlasher = async () => {
    if (isFlashing) {
      try {
        let curr = 0;
        for (let i = 0; i < paragraphIndex; i++) {
          for (const str of paragraphs[i]) {
            curr += str.split(" ").length;
          }
        }
        for (let i = 0; i < phrasesIndex; i++) {
          curr += paragraphs[paragraphIndex][i].split(" ").length;
        }
        await fetch(`${API_BASE_URL}/api/user/updateBookProgress`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ uuid: uuid, newValue: curr }),
        });
        setUserData((ud) => ({
          ...ud,
          progress: { ...ud.progress, [uuid]: curr },
        }));
      } catch (error) {
        console.error("Error updating book progress:", error);
      }
    }
    setIsFlashing(!isFlashing);
  };

  const IncrementArticleDown = () => {
    console.log(articleIndex);
    let prevArticleIndex = articleIndex;
    if (articleIndex - 4 >= 0) {
      setArticleIndex(articleIndex - 4);
      setEndArticleIndex(endArticleIndex - 4);
    } else {
      setArticleIndex(0);
      setEndArticleIndex(endArticleIndex - prevArticleIndex);
    }
    console.log(articleIndex);
  };

  const IncrementArticleUp = () => {
    console.log(articleIndex);
    if (articleIndex + 4 < articles.length) {
      setArticleIndex(articleIndex + 4);
      setEndArticleIndex(endArticleIndex + 4);
    } else {
      setArticleIndex(articles.length - 4);
      setEndArticleIndex(articles.length - 1);
    }
    console.log(articleIndex);
  };

  // In the Workout view, we now only render paragraphs starting from the current paragraph index.
  const renderExercise = () => {
    if (exerciseStep === "Select Level") {
      return (
        <>
          <h3>Pick Your Level</h3>
          <div>
            {levels.map((level, index) => (
              <button
                key={level}
                className={`duration-button ${levelSelection === level ? "selected" : ""}`}
                onClick={() => setLevelSelection(level)}
                disabled={!isLevelUnlocked(index + 1)}
                style={{
                  backgroundColor: isLevelUnlocked(index + 1) ? "#4CAF50" : "#CCCCCC",
                  cursor: isLevelUnlocked(index + 1) ? "pointer" : "not-allowed",
                }}
              >
                {level}
              </button>
            ))}
          </div>
          <button className="next-button" onClick={() => setExerciseStep("Upload File")}>
            Next
          </button>
        </>
      );
    }
    if (exerciseStep === "Upload File") {
      return (
        <div className="file-upload-container">
          <h3>Upload or Choose from Library</h3>
          <div>
            <img src={UploadButton} alt="upload button" />{" "}
            {/* NEXT STEP: file upload function */}
            <img
              src={LibraryButton}
              alt="library button"
              onClick={goToLibrary}
              className="library-button"
            />
          </div>
        </div>
      );
    }
    if (exerciseStep === "Choose Article") {
      return (
        <div>
          <h3>Choose an Unread Article</h3>
          <div className="article-picker">
            <img src={DownButton} className="library-button" onClick={IncrementArticleDown} />
            {articles.slice(articleIndex, endArticleIndex).map((article) =>
              userData.completedBooks.includes(article) ? (
                <>
                  <p className="text-overlay">completed</p>
                  <img
                    src={"data:image/jpeg;base64," + bookData[article].cover}
                    alt={bookData[article].title}
                    className={"bookCover read"}
                  />
                </>
              ) : (
                <img
                  key={article}
                  src={"data:image/jpeg;base64," + bookData[article].cover}
                  alt={bookData[article].title}
                  className={"bookCover"}
                  onClick={() => clickBookModern(article)}
                  width="200px"
                  height="200px"
                />
              )
            )}
            <img src={UpButton} className="library-button" onClick={IncrementArticleUp} />
          </div>
        </div>
      );
    }
    if (exerciseStep === "Workout") {
      // Only render paragraphs that haven't been read.
      return (
        <div>
          {paragraphs.slice(paragraphIndex).map((paragraph, idx) => {
            // idx + paragraphIndex gives the actual paragraph index.
            const actualPIndex = idx + paragraphIndex;
            return (
              <div className="article-reader-9" key={`paragraph-${actualPIndex}`}>
                {paragraph.map((word, wIdx) => (
                  <div
                    key={`word-${wIdx}`}
                    className={`phrase ${
                      isFlashing &&
                      actualPIndex === paragraphIndex &&
                      wIdx === phrasesIndex
                        ? "white-text"
                        : ""
                    }`}
                  >
                    <div
                      className={`${
                        isFlashing &&
                        actualPIndex === paragraphIndex &&
                        wIdx === phrasesIndex
                          ? "text-cover-9"
                          : ""
                      }`}
                    ></div>
                    {word}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <div className="exercise-container">
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="close-icon-wrapper">
          <img src={PlayButton} alt="play button" onClick={playWordFlasher} width="50px" />
        </div>
      </div>
      <div className="main-content-8">{renderExercise()}</div>
      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        {exerciseStep !== "Workout" ? (
          <p>Upload or choose an article you have not read before.</p>
        ) : (
          <p>Focus on the flashing words.</p>
        )}
      </div>
    </div>
  );
};

export default Exercise9Workout;